import {ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {jwtDecode} from "jwt-decode";
import {TokenInterface} from "@app/Interfaces/token.interface";
import {DropdownComponent} from "@app/dropdown/dropdown.component";
import {DataService} from "@app/shared/data.service";
import {UserRolesService} from "@app/services/user-roles.service";
import {QuarantineApiService} from "@app/quarantine/services/quarantine.api.service";
import {DocumentTypeInterfaces} from "@app/quarantine/intrfaces/quarantine-form.interface";
import {HelpersQuarantineService} from "@app/quarantine/services/helpers.service.service";
import {
  OrganisationInterface, QuarantineDocumentPlacement,
  QuarantinePlacementFormInterface,
  QuarantinePlacementInterface, SelectedMedicineMedicalDevicesInterface, UpdateDocumentQuarantinePlacementInterface
} from "@app/quarantine/intrfaces/quarantine.interface";
import {catchError, take} from "rxjs";
import {CreateTableComponent} from "@app/quarantine/shared/create-table/create-table.component";
import {
  QuarantinePlacementFooterComponent
} from "@app/quarantine/shared/quarantine-placement-footer/quarantine-placement-footer.component";
import {formatDate, NgForOf, NgIf, NgSwitch, NgSwitchCase} from "@angular/common";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {DocumentStatusHighlightDirective} from "@app/quarantine/shared/directives/status-hilight.directive";
import {
  EditQuarantinePlacementTableComponent
} from "@app/quarantine/shared/edit-quarantine-placement-table/edit-quarantine-placement-table.component";
import {DateInputConverter} from "@app/quarantine/shared/directives/data-input-type.directive";
import {ModalConfirmComponent} from "@app/quarantine/shared/modal-confirm/modal-confirm.component";
import {ToastService} from "@app/toast/toast.service";
import {LoadingAnimationComponent} from "@app/loading-animation/loading-animation.component";

@Component({
  selector: 'app-quarantine-placement-form',
  templateUrl: './quarantine-placement-form.component.html',
  styleUrls: ['./quarantine-placement-form.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DropdownComponent,
    CreateTableComponent,
    QuarantinePlacementFooterComponent,
    NgIf,
    DocumentStatusHighlightDirective,
    EditQuarantinePlacementTableComponent,
    DateInputConverter,
    ModalConfirmComponent,
    NgForOf,
    LoadingAnimationComponent,
    NgSwitch,
    NgSwitchCase
  ]
})
export class QuarantinePlacementFormComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  
  @ViewChild(ModalConfirmComponent) modalConfirm!: ModalConfirmComponent
  
  @Input() config: QuarantinePlacementFormInterface | undefined = undefined;
  
  form!: FormGroup;
  token: TokenInterface = jwtDecode(localStorage['access_token']);
  showSubInstitutions: boolean = false;
  showDifferentDocType: boolean = false;

  documentTypes: DocumentTypeInterfaces = this.helpersQuarantineService.docTypes;
  documentTypesForForm: string[] = [];
  defaultDocumentTypesSelected: string[] = [];

  currentOrganisation: OrganisationInterface | undefined;
  organisationChildrenForForm: string[] = [];
  organisationChildrenForFormSelected: string[] = [];
  
  quarantinePlacement: QuarantineDocumentPlacement | undefined;
  
  statusTypes:{ [key: string]: string } = this.helpersQuarantineService.statusTypes;
  placementTypes:{ [key: string]: string } = this.helpersQuarantineService.placementTypes;
  
  organisationsTypesList: { [key: string]: string }[] = []
  
  pageTitle  = ''
  
  isCreateQuarantineRemoval: boolean = false;
  isCreateQuarantinePlacement: boolean = false;
  isEditQuarantineRemoval: boolean = false;
  isEditQuarantinePlacement: boolean = false;
  
  showRefreshButton= false;
  isRefreshing = false;
  
  institutionIdsForSearch: any = []
  
  canMakeActions = false;
  
  documentPlacementId: number | string | undefined;
  
  isLoading = false;


  userData = {
    name: this.token.name,
    organizationId: this.token.organizationInfo.organizationId,
    organizationName: this.token.organizationInfo.legalForm,
    organizationCode: this.token.organizationInfo.code
  }

  constructor(
      private fb: FormBuilder,
      private dataService: DataService,
      private quarantineApiService: QuarantineApiService,
      private helpersQuarantineService: HelpersQuarantineService,
      private router: Router,
      private toastService: ToastService,
  ) {
    this.initForm();
  }


  ngOnInit() {
    if (
        (!history.state.quarantinePlacement && this.config?.stage !== 'CREATE') ||
        (!history.state.quarantinePlacement && this.config?.documentType === 'QUARANTINE_REMOVAL')
    ) {
      this.router.navigate(['quarantine']);
    }
    
    this.isCreateQuarantineRemoval = this.config?.documentType === 'QUARANTINE_REMOVAL' && this.config?.stage === 'CREATE';
    this.isCreateQuarantinePlacement = this.config?.documentType === 'QUARANTINE_PLACEMENT' && this.config.stage === 'CREATE';
    this.isEditQuarantineRemoval = this.config?.documentType === 'QUARANTINE_REMOVAL' && this.config?.stage === 'EDIT';
    this.isEditQuarantinePlacement = this.config?.documentType === 'QUARANTINE_PLACEMENT' && this.config.stage === 'EDIT';
    
    if (!(this.isCreateQuarantineRemoval || this.isCreateQuarantinePlacement)) {
      this.quarantinePlacement = history.state.quarantinePlacement
    }
    
    this.canMakeActions = this.quarantinePlacement?.organizationId === Number.parseInt(this.userData.organizationId);
    
    this.getCurrentOrganisation()
    this.subscribeToOrganisationChanges();
    
    this.setPageTitle();
    
    this.fillFom(this.quarantinePlacement);
    this.showRefreshButton = this.quarantinePlacement?.documentStatus === 'DRAFT' && this.quarantinePlacement?.transactionType === 'QUARANTINE_PLACEMENT';
  }
  
  getCurrentOrganisation() {
    this.helpersQuarantineService
        .getOrganisation(Number(this.token.organizationInfo.organizationId))
        .pipe(take(1))
        .subscribe((data) => {
          if (!data) {
            return;
          }
          this.currentOrganisation = data;
          this.initQuarantineOrganizationInput()
          
          
          this.organisationChildrenForForm = this.currentOrganisation?.children
              .filter((org) => org.type === 'ZOZ')
              .reduce((acc: string[], cur) => {
                acc.push(cur.fullName);
                return acc;
              }, [])
        })
  }
  
  subscribeToOrganisationChanges() {
    this.form.controls['quarantineOrganization'].valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          if (value === 'OTHER') {
            this.showSubInstitutions = true;
            this.form.controls['institutionIds'].enable();
          } else {
            this.showSubInstitutions = false;
            this.form.controls['institutionIds'].disable();
          }
          
          switch (value) {
            case 'OTHER': {
              this.institutionIdsForSearch = this.form.controls['institutionIds'].value
              break;
            }
            case 'ALL_SUBORDINATED': {
              // @ts-ignore
              this.institutionIdsForSearch = [...this.currentOrganisation?.children
                  .filter((el) =>  el.type === 'ZOZ')
                  .map((el) => el.id)];
              break;
            }
            case 'ALL': {
              this.institutionIdsForSearch = [];
            }
            
          }
       
    })
  }
  //
  // documentSelect(data: string) {
  //   let doc = this.documentTypes.find((doc) => doc.nameUa === data);
  //   this.form.controls['documentType'].setValue(doc?.name);
  // }
  
  selectChildOrganisation(selected: string) {
    let arr = selected.split('&');
    if (arr.length === 1 && arr[0] === '') {
      this.form.controls['institutionIds'].setValue([]);
      this.institutionIdsForSearch = [];
    } else {
      let res: any = [];
      this.currentOrganisation?.children.forEach(child => {
        if (arr.some(sel => sel === child.fullName)) {
          res.push(child.id);
        }
      })
      this.form.controls['institutionIds'].setValue([...res]);
      this.institutionIdsForSearch = res;
    }
  }
  
  initForm() {
    this.form = this.fb.group({
      documentType: ['', Validators.required],
      differentDocType: ['', Validators.required],
      documentNumber: ['', Validators.required],
      orderDate: ['', Validators.required],
      comment: [''],
      quarantineOrganization: ['', Validators.required],
      quarantineScope: [null, Validators.required],
      institutionIds: [[], Validators.required],
    });
    this.form.controls['institutionIds'].disable();
    this.form.controls['differentDocType'].disable();
    
    this.subscribeToDocumentTypeChanges();
  }
  
  subscribeToDocumentTypeChanges() {
    this.form.controls['documentType'].valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value: string) => {
          if (value === 'OTHER') {
            this.form.controls['differentDocType'].enable();
            this.showDifferentDocType = true;
          } else {
            this.form.controls['differentDocType'].disable();
            this.showDifferentDocType = false;
          }
        })
  }
  
  fillFom(initialData: QuarantineDocumentPlacement | undefined) {
    if (this.isCreateQuarantineRemoval || this.isEditQuarantineRemoval) {
      this.form.controls['quarantineOrganization'].disable();
      this.form.controls['quarantineScope'].disable();
      this.form.controls['institutionIds'].disable();
    }
    
    if (!initialData || this.isCreateQuarantineRemoval) {
      return;
    }
    
    this.form.patchValue({
      documentType: initialData.documentType,
      documentNumber: initialData.documentNumber,
      differentDocType: initialData.differentDocType,
      orderDate: new Date(initialData.orderDate),
      comment: initialData.comment,
      quarantineOrganization: initialData.quarantineOrganization,
      quarantineScope: initialData.quarantineScope,
      institutionIds: initialData.institutionIds,
    })
    
    // this.defaultDocumentTypesSelected = this.documentTypes.filter((doc) => doc.name === initialData.documentType).map((doc) => doc.nameUa);
    // @ts-ignore
    this.organisationChildrenForFormSelected = this.currentOrganisation?.children.reduce((acc: string[], cur) => {
      if (initialData.institutionIds?.includes(cur.id)) {
        acc.push(cur.fullName);
      }
      return acc;
    }, []);
    
    if (
        (!this.canMakeActions && this.quarantinePlacement?.documentStatus === 'DRAFT') ||
        (this.quarantinePlacement?.documentStatus === 'CONFIRMED' && this.quarantinePlacement?.transactionType == 'QUARANTINE_PLACEMENT') ||
        (this.quarantinePlacement?.documentStatus === 'CONFIRMED' && this.quarantinePlacement?.transactionType == 'QUARANTINE_REMOVAL') ||
        (this.quarantinePlacement?.documentStatus === 'CANCELLED' && this.quarantinePlacement?.transactionType == 'QUARANTINE_REMOVAL') ||
        (this.quarantinePlacement?.documentStatus === 'CANCELLED' && this.quarantinePlacement?.transactionType == 'QUARANTINE_PLACEMENT')
    ) {
      this.form.disable();
    }
  }
  
  initQuarantineOrganizationInput() {
    if ((this.isEditQuarantineRemoval || this.isEditQuarantinePlacement) && !this.canMakeActions) {
      this.organisationsTypesList = [...this.helpersQuarantineService.quarantineOrganizationsType];
      return;
    }
    
    if (this.currentOrganisation?.type === 'MOZ') {
      this.organisationsTypesList = [...this.helpersQuarantineService.quarantineOrganizationsType]
    }

    
    if (this.currentOrganisation?.type === 'DOZ') {
      this.organisationsTypesList = this.helpersQuarantineService.quarantineOrganizationsType
          .filter((el) => !['ALL'].includes(el['value']));
    }
    
    if (this.currentOrganisation?.type === 'ZOZ') {
      this.organisationsTypesList = this.helpersQuarantineService.quarantineOrganizationsType
          // @ts-ignore
          .filter((el) => !['ALL', 'ALL_SUBORDINATED'].includes(el['value']))
      this.form.controls['quarantineOrganization'].setValue('OTHER');
      this.form.controls['institutionIds'].enable();
    }
  }
  
  getQuarantinePlacement() {
    this.isLoading = true;
    
    this.quarantineApiService
        // @ts-ignore
        .getQuarantinePlacement(this.quarantinePlacement?.id)
        .pipe(
            catchError(err => {
              console.log(err);
              if (err && err.error && err.error.messages && err.error.messages.length) {
                this.toastService.showError(err.error.messages[1]);
              } else {
                this.toastService.showError();
              }
              this.isLoading = false;
              return err;
            })
        )
        // @ts-ignore
        .subscribe((placement: QuarantineDocumentPlacement) => {
          this.isLoading = false;
          this.quarantinePlacement = placement;
        })
    
  }
  
  createPlacement() {
    this.form.markAllAsTouched();
    
    if(this.form.controls['quarantineScope'].invalid) {
      this.toastService.showError('Неможливо створити Чернетку розміщення у карантин. Укажіть перелік медичних товарів, які мають бути розміщені у карантин, щоб продовжити.')
    }
    
    if (!this.form.valid) {
      return;
    }
    this.isLoading = true;
    if (this.isCreateQuarantinePlacement) {
      const placementData: QuarantinePlacementInterface = {...this.form.value}
      placementData.orderDate = new Date(placementData.orderDate).toISOString();
      
      this.quarantineApiService
          .createQuarantinePlacement(placementData)
          .pipe(
              catchError(err => {
                console.log(err);
                if (err && err.error && err.error.messages && err.error.messages.length) {
                  this.toastService.showError(err.error.messages[1]);
                } else {
                  this.toastService.showError();
                }
                this.isLoading = false;
                return err;
              })
          )
          .subscribe(() => {
            this.toastService.showSuccess('Запис успішно створено, але він потребує підтвердження, щоб провести операцію розміщення у карантин у системі. Перейдіть у документ з реєстру щоб підтвердити.')
            this.isLoading = false;
            this.returnToList()
          });
    } else {
      let data = {
        ...this.form.value
      }
      
      data['parentDocumentId'] = this.history.state.quarantinePlacement?.id;
      data.orderDate = new Date(data.orderDate).toISOString();
      
      this.quarantineApiService.createDocumentQuarantineRemoval(data)
          .pipe(
              catchError(err => {
                console.log(err);
                if (err && err.error && err.error.messages && err.error.messages.length) {
                  this.toastService.showError(err.error.messages[1]);
                } else {
                  this.toastService.showError();
                }
                this.isLoading = false;
                return err;
              })
          )
          .subscribe(() => {
            this.toastService.showSuccess('Запис успішно створено, але він потребує підтвердження, щоб провести операцію зняття з карантину у системі. Перейдіть у документ з реєстру щоб підтвердити.')
            this.isLoading = false;
            this.returnToList();
          })
      
    }
  }
  
  confirmPlacement() {
    this.isLoading = true;
    if (this.isEditQuarantinePlacement) {
      this.quarantineApiService.confirmDocumentQuarantinePlacement(this.quarantinePlacement?.id)
          .pipe(catchError(err => {
            console.log(err);
            if (err && err.error && err.error.messages && err.error.messages.length) {
              this.toastService.showError(err.error.messages[1]);
            } else {
              this.toastService.showError();
            }
            this.isLoading = false;
            return err;
          }))
          .subscribe(() => {
            this.toastService.showSuccess('Запис успішно підтверджено. Зважайте на те, що кількість медичних товарів до розміщення у карантин для деяких закладів могла змінитись у зв\'язку з обліковими операціями всередині закладу')
            this.isLoading = false;
            this.returnToList();
          })
    } else {
      this.quarantineApiService.confirmDocumentQuarantineRemoval(this.quarantinePlacement?.id)
          .pipe(catchError(err => {
            console.log(err);
            if (err && err.error && err.error.messages && err.error.messages.length) {
              this.toastService.showError(err.error.messages[1]);
            } else {
              this.toastService.showError();
            }
            this.isLoading = false;
            return err;
          }))
          .subscribe(() => {
            this.toastService.showSuccess('Запис успішно підтверджено. Зважайте на те, що кількість медичних товарів до зняття з карантину могла змінитись у зв\'язку з тим, що заклади могли самостійно зняти з карантину свої медичні товари.')
            this.isLoading = false;
            this.returnToList();
          })
    }
    

  }
  
  cancelPlacement() {
    this.isLoading = true;
    if (this.isEditQuarantinePlacement) {
      this.quarantineApiService.canselDocumentQuarantinePlacement(this.quarantinePlacement?.id)
          .pipe(catchError(err => {
            console.log(err);
            if (err && err.error && err.error.messages && err.error.messages.length) {
              this.toastService.showError(err.error.messages[1]);
            } else {
              this.toastService.showError();
            }
            this.isLoading = false;
            return err;
          }))
          .subscribe(() => {
            this.toastService.showSuccess('Запис успішно скасовано')
            this.isLoading = false;
            this.returnToList();
          })
    } else {
      this.quarantineApiService.canselDocumentQuarantineRemoval(this.quarantinePlacement?.id)
          .pipe(catchError(err => {
            console.log(err);
            if (err && err.error && err.error.messages && err.error.messages.length) {
              this.toastService.showError(err.error.messages[1]);
            } else {
              this.toastService.showError();
            }
            this.isLoading = false;
            return err;
          }))
          .subscribe(() => {
            this.toastService.showSuccess('Запис успішно скасовано')
            this.isLoading = false;
            this.returnToList();
          })
    }

  }
  
  savePlacement() {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }
    
    this.isLoading = true;
    if (this.isEditQuarantinePlacement) {
      const placementData: UpdateDocumentQuarantinePlacementInterface = {...this.form.value}
      placementData.orderDate = new Date(placementData.orderDate).toISOString();
      placementData.id = <number>this.quarantinePlacement?.id
      
      this.quarantineApiService.updateDocumentQuarantinePlacement(placementData)
          .pipe(catchError(err => {
            console.log(err);
            if (err && err.error && err.error.messages && err.error.messages.length) {
              this.toastService.showError(err.error.messages[1]);
            } else {
              this.toastService.showError();
            }
            this.isLoading = false;
            return err;
          }))
          .subscribe(() => {
            this.toastService.showSuccess('Запис успішно оновлено, але він потребує підтвердження')
            this.isLoading = false;
            this.returnToList();
          })
    } else {
      let data = {
        ...this.form.value
      }
      
      data['id'] = this.history.state.quarantinePlacement?.id;
      data.orderDate = new Date(data.orderDate).toISOString();
      
      this.quarantineApiService.updateDocumentQuarantineRemoval(data)
          .pipe(
              catchError(err => {
                console.log(err);
                this.isLoading = false;
                if (err && err.error && err.error.messages && err.error.messages.length) {
                  this.toastService.showError(err.error.messages[1]);
                } else {
                  this.toastService.showError();
                }
                
                return err;
              })
          )
          .subscribe(() => {
            this.toastService.showSuccess('Запис успішно оновлено, але він потребує підтвердження')
            this.isLoading = false;
            this.returnToList();
          })
    }
    

  }
  
  returnToList() {
    this.router.navigate(['quarantine']);
  }
  
  openModalConfirm() {
    this.modalConfirm.openModal();
  }
  
  redirectToCreateQuarantineRemoval () {
    this.router.navigate(['quarantine/create-quarantine-removal'], {
      state :  {
        quarantinePlacement: this.quarantinePlacement
      }
    });
  }
  
  refreshTransactionDocument (data: SelectedMedicineMedicalDevicesInterface[]) {
    this.getQuarantinePlacement()
    this.form.controls['quarantineScope'].setValue(data);
  }
  
  refreshList() {
    this.isRefreshing = true;
    setTimeout(() => {
      this.isRefreshing = false;
    }, 1000)
  }
  
  get isDraftDocument () {
    return this.quarantinePlacement?.documentStatus === 'DRAFT';
  }
  
  get isNewPlacementTable() {
    return this.config?.stage === 'CREATE' && this.config?.documentType === 'QUARANTINE_PLACEMENT';
  }
  
  get documentStatus() {
    return this.quarantinePlacement?.documentStatus || 'DRAFT';
  }
  
  get userName() {
    return this.quarantinePlacement ?
        `${this.quarantinePlacement?.createdBy.firstName} ${this.quarantinePlacement?.createdBy.lastName}`
        : this.userData.name;
  }
  
  setPageTitle(): void {
    switch (true) {
      case this.isCreateQuarantinePlacement :{
        this.pageTitle = 'Створити розміщення у карантин';
        break;
      }
      case this.isCreateQuarantineRemoval :{
        this.pageTitle = 'Створити зняття з карантину';
        break;
      }
      case this.isEditQuarantinePlacement && this.quarantinePlacement?.documentStatus === 'DRAFT' :{
        this.pageTitle = `Керувати розміщенням у карантин №${this.quarantinePlacement?.id}`;
        break
      }
      case this.isEditQuarantinePlacement && (this.quarantinePlacement?.documentStatus === 'CONFIRMED' || this.quarantinePlacement?.documentStatus === 'CANCELLED') :{
        this.pageTitle = `Розміщення у карантин №${this.quarantinePlacement?.id}`;
        break
      }
      case this.isEditQuarantineRemoval && this.quarantinePlacement?.documentStatus === 'DRAFT' :{
        this.pageTitle = `Керувати зняттям з карантину №${this.quarantinePlacement?.id}`;
        break;
      }
      case this.isEditQuarantineRemoval && (this.quarantinePlacement?.documentStatus === 'CONFIRMED' || this.quarantinePlacement?.documentStatus === 'CANCELLED') :{
        this.pageTitle = `Зняття з карантину №${this.quarantinePlacement?.id}`;
        break;
      }
    }
  }
  
  get totalAmountText() {
    switch (true) {
      case this.isEditQuarantinePlacement && this.quarantinePlacement?.documentStatus === 'DRAFT': {
        return 'До розміщення у карантин';
      }
      default: {
        return 'Всього у карантині';
      }
    }
  }
  
  get isVisibleTotalAmount() {
    return this.isEditQuarantinePlacement && (this.quarantinePlacement?.documentStatus === 'DRAFT' || this.quarantinePlacement?.documentStatus === 'CONFIRMED');
  }
  
  get editName() {
    if (this.quarantinePlacement && this.quarantinePlacement?.modifiedBy?.firstName) {
      return `${this.quarantinePlacement?.modifiedBy.firstName} ${this.quarantinePlacement?.modifiedBy.lastName}`
    }
    return ''
  }
  
  protected readonly history = history;
}
